/**
 * Funnel Settings Tab (MA-UI-059)
 * 
 * Funnel-specific runtime and compliance settings.
 * 
 * Sections:
 * - Compliance (Primary - TrustedForm, Consent)
 * - Runtime (Scaffolded)
 * - Booking (Scaffolded)
 * - Integrations (Scaffolded)
 * 
 * MA-ADMIN-001: Now displays global TrustedForm API key status and links to Admin > Compliance
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  Shield, 
  ShieldCheck,
  Settings2,
  Calendar,
  Plug2,
  Sparkles,
  Database,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  Info,
  CheckCircle,
  CheckCircle2,
  XCircle,
  FileText,
  Lock,
  ExternalLink,
  Key,
  RefreshCw,
  Loader2,
  Tags,  // MA-IMPL-117: For Attribution section
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';

// MA-UI-109: Data Enrichment Section (extracted to separate component)
import DataEnrichmentSection from './DataEnrichmentSection';

// MA-ADMIN-013: Consent Version Catalog
import ConsentVersionCatalog from './ConsentVersionCatalog';

// MA-IMPL-117: Attribution Section for campaign assignment
import FunnelAttributionSection from './FunnelAttributionSection';

// =============================================================================
// Section Navigation Component
// =============================================================================
function SettingsNav({ activeSection, onSectionChange }) {
  const sections = [
    { id: 'compliance', label: 'Compliance', icon: Shield, badge: 'Primary' },
    { id: 'attribution', label: 'Attribution', icon: Tags },  // MA-IMPL-117: Campaign & Attribution
    { id: 'runtime', label: 'Runtime', icon: Settings2 },
    { id: 'booking', label: 'Booking', icon: Calendar },
    { id: 'integrations', label: 'Integrations', icon: Plug2 },
    { id: 'enrichment', label: 'Data Enrichment', icon: Database },  // MA-UI-109
  ];
  
  return (
    <div className="w-56 flex-shrink-0 border-r border-white/10 pr-4">
      <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 px-2">
        Settings
      </h3>
      <nav className="space-y-1">
        {sections.map((section) => (
          <button
            key={section.id}
            onClick={() => onSectionChange(section.id)}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
              activeSection === section.id
                ? "bg-indigo-500/20 text-white"
                : "text-slate-400 hover:text-white hover:bg-white/5"
            )}
            data-testid={`settings-nav-${section.id}`}
          >
            <section.icon className="h-4 w-4" />
            <span className="flex-1 text-left">{section.label}</span>
            {section.badge && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400">
                {section.badge}
              </span>
            )}
          </button>
        ))}
      </nav>
    </div>
  );
}

// =============================================================================
// Compliance Section (Primary Implementation)
// MA-COMPLIANCE-008: Simplified UX with single card layout
// MA-ADMIN-001: Now shows global TrustedForm API key status
// =============================================================================
function ComplianceSection({ settings, onSettingsChange, funnelId }) {
  // MA-ADMIN-001: Fetch global TrustedForm API key status
  const [globalSettings, setGlobalSettings] = useState(null);
  const [loadingGlobal, setLoadingGlobal] = useState(true);
  
  const fetchGlobalSettings = useCallback(async () => {
    setLoadingGlobal(true);
    try {
      const res = await api.get('/settings/global');
      setGlobalSettings(res.data);
    } catch {
      // Silent fail - will show "unknown" status
      setGlobalSettings(null);
    } finally {
      setLoadingGlobal(false);
    }
  }, []);
  
  useEffect(() => {
    fetchGlobalSettings();
  }, [fetchGlobalSettings]);
  
  const handleChange = (key, value) => {
    onSettingsChange({
      ...settings,
      compliance: {
        ...settings.compliance,
        [key]: value,
      },
    });
  };
  
  // Handle TrustedForm mode change - stores ONLY trustedformMode (single source of truth)
  const handleTrustedFormModeChange = (mode) => {
    // MA-COMPLIANCE-007: Verify single source of truth
    if (process.env.NODE_ENV === 'development') {
      console.assert(!('enableTrustedForm' in (settings.compliance || {})), 
        'MA-COMPLIANCE-007: Legacy enableTrustedForm field detected!');
    }
    
    onSettingsChange({
      ...settings,
      compliance: {
        ...settings.compliance,
        trustedformMode: mode,
        // MA-COMPLIANCE-007: Remove legacy fields on write
        enableTrustedForm: undefined,
        missingCertBehavior: undefined,
      },
    });
  };
  
  const compliance = settings?.compliance || {};
  
  // MA-COMPLIANCE-007: trustedformMode is the SINGLE source of truth
  const trustedformMode = compliance.trustedformMode || 'off';
  
  // MA-ADMIN-001: Global TrustedForm status
  const globalTrustedform = globalSettings?.trustedform || {};
  const isGlobalKeyConfigured = globalTrustedform.api_key_set;
  const globalConnectionStatus = globalTrustedform.connection_status || 'not_configured';
  
  // MA-ADMIN-001: Show warning if enforce mode + no global key
  const showEnforceWarning = trustedformMode === 'enforce' && !isGlobalKeyConfigured;
  
  return (
    <div className="space-y-6" data-testid="compliance-section">
      {/* Section Header */}
      <div>
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-indigo-400" />
          <h2 className="text-xl font-bold text-slate-100">Compliance</h2>
        </div>
        <p className="text-slate-200/60 text-sm mt-1">
          Configure TrustedForm and consent requirements for this funnel
        </p>
      </div>

      {/* MA-ADMIN-001: Global TrustedForm API Key Status Card */}
      <Card className={cn(
        "border",
        isGlobalKeyConfigured && globalConnectionStatus === 'connected'
          ? "bg-emerald-500/5 border-emerald-500/20"
          : !isGlobalKeyConfigured
            ? "bg-amber-500/5 border-amber-500/20"
            : "bg-white/5 border-white/10"
      )} data-testid="global-trustedform-status-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className={cn(
                "h-8 w-8 rounded-lg flex items-center justify-center",
                isGlobalKeyConfigured && globalConnectionStatus === 'connected'
                  ? "bg-emerald-500/20"
                  : !isGlobalKeyConfigured
                    ? "bg-amber-500/20"
                    : "bg-white/10"
              )}>
                {loadingGlobal ? (
                  <Loader2 className="h-4 w-4 text-slate-400 animate-spin" />
                ) : isGlobalKeyConfigured && globalConnectionStatus === 'connected' ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                ) : !isGlobalKeyConfigured ? (
                  <Key className="h-4 w-4 text-amber-400" />
                ) : (
                  <XCircle className="h-4 w-4 text-red-400" />
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-slate-200">
                  Global TrustedForm API Key
                </p>
                {loadingGlobal ? (
                  <p className="text-xs text-slate-500">Loading...</p>
                ) : isGlobalKeyConfigured ? (
                  <div className="flex items-center gap-2">
                    <Badge className={cn(
                      "text-xs",
                      globalConnectionStatus === 'connected'
                        ? "bg-emerald-500/20 text-emerald-300"
                        : globalConnectionStatus === 'failed'
                          ? "bg-red-500/20 text-red-300"
                          : "bg-amber-500/20 text-amber-300"
                    )}>
                      {globalConnectionStatus === 'connected' ? 'Connected' : 
                       globalConnectionStatus === 'failed' ? 'Connection Failed' : 'Not Tested'}
                    </Badge>
                    {globalTrustedform.api_key_masked && (
                      <code className="text-xs text-slate-500 font-mono">
                        {globalTrustedform.api_key_masked}
                      </code>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-amber-400">No API key configured</p>
                )}
              </div>
            </div>
            <Link to="/app/admin?tab=compliance">
              <Button variant="outline" size="sm" data-testid="link-to-admin-compliance">
                <ExternalLink className="h-3 w-3 mr-1" />
                Manage in Admin
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* MA-ADMIN-001: Warning when enforce mode but no global key */}
      {showEnforceWarning && (
        <div 
          className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-start gap-3"
          data-testid="enforce-no-key-warning"
        >
          <AlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-200">
              No Global TrustedForm API Key Configured
            </p>
            <p className="text-xs text-amber-300/80 mt-1">
              This funnel is set to <span className="font-semibold">Enforce Compliance</span>, but no TrustedForm API key has been configured.
              Leads will remain <span className="font-semibold">quarantined</span> until verification is available.
            </p>
            <Link to="/app/admin?tab=compliance" className="inline-block mt-2">
              <Button variant="outline" size="sm" className="bg-amber-500/10 border-amber-500/30 text-amber-200 hover:bg-amber-500/20">
                <Key className="h-3 w-3 mr-1" />
                Configure API Key
              </Button>
            </Link>
          </div>
        </div>
      )}

      {/* MA-COMPLIANCE-008: Single cohesive TrustedForm card */}
      <Card className="glass border-white/10">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-slate-100 text-base">
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
            TrustedForm Settings
          </CardTitle>
          {/* MA-COMPLIANCE-008: Inline info (replaces separate card) */}
          <p className="text-sm text-slate-400 mt-2">
            MissionAssist captures TrustedForm certificates automatically.
            {trustedformMode !== 'off' && (
              <> To use compliance enforcement, enable <span className="text-indigo-300">Auto-Retain</span> in your ActiveProspect account.</>
            )}
          </p>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Mode Selector */}
          <div className="space-y-2">
            <Label htmlFor="trustedform-mode" className="text-slate-100 font-medium text-sm">
              TrustedForm Mode
            </Label>
            <Select
              value={trustedformMode}
              onValueChange={handleTrustedFormModeChange}
            >
              <SelectTrigger 
                className="bg-white/5 border-white/10 text-slate-100 w-full max-w-sm"
                data-testid="select-trustedform-mode"
              >
                <SelectValue placeholder="Select mode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="off">
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-slate-400" />
                    <span>Off</span>
                  </div>
                </SelectItem>
                <SelectItem value="capture_only">
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-blue-400" />
                    <span>Capture Only</span>
                  </div>
                </SelectItem>
                <SelectItem value="enforce">
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-emerald-400" />
                    <span>Enforce Compliance</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* ================================================================= */}
          {/* MODE: OFF - Simple inline message */}
          {/* ================================================================= */}
          {trustedformMode === 'off' && (
            <p className="text-sm text-slate-400 py-2">
              TrustedForm is disabled. All leads are eligible for outreach.
            </p>
          )}
          
          {/* ================================================================= */}
          {/* MODE: CAPTURE_ONLY - Inline message + Setup */}
          {/* ================================================================= */}
          {trustedformMode === 'capture_only' && (
            <>
              <p className="text-sm text-slate-300">
                Certificates are captured for record-keeping only. 
                <span className="text-blue-300"> Leads will NOT be restricted</span> based on certificate status.
              </p>
              
              {/* Simplified Setup Section */}
              <TrustedFormSetupSection />
            </>
          )}
          
          {/* ================================================================= */}
          {/* MODE: ENFORCE - Warning + Behavior + Setup */}
          {/* ================================================================= */}
          {trustedformMode === 'enforce' && (
            <>
              {/* MA-COMPLIANCE-008: Inline warning (not large card) */}
              <p className="text-sm text-amber-300">
                Leads without a verified TrustedForm certificate will be <span className="font-semibold">quarantined until compliance is verified</span>.
              </p>
              
              {/* MA-COMPLIANCE-008: Compact system behavior (not a card) */}
              <div className="py-3 border-y border-white/5">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">System Behavior</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                    <span className="text-slate-400">Missing certificate</span>
                    <span className="text-slate-500 mx-1">→</span>
                    <span className="text-amber-300">Quarantined</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                    <span className="text-slate-400">Pending verification</span>
                    <span className="text-slate-500 mx-1">→</span>
                    <span className="text-amber-300">Quarantined</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                    <span className="text-slate-400">Not retained</span>
                    <span className="text-slate-500 mx-1">→</span>
                    <span className="text-amber-300">Quarantined</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                    <span className="text-slate-400">Retained (verified)</span>
                    <span className="text-slate-500 mx-1">→</span>
                    <span className="text-emerald-300">Eligible</span>
                  </div>
                </div>
                {/* MA-COMPLIANCE-008: Manual override clarity */}
                <p className="text-xs text-slate-500 mt-3">
                  Quarantined leads can be manually approved for engagement after verification.
                </p>
              </div>
              
              {/* Simplified Setup Section */}
              <TrustedFormSetupSection />
            </>
          )}
        </CardContent>
      </Card>

      {/* Consent Requirements Card */}
      <Card className="glass border-white/10">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-slate-100 text-base">
            <FileText className="w-5 h-5 text-blue-400" />
            Consent Requirements
          </CardTitle>
          <CardDescription className="text-slate-400 text-sm">
            Configure explicit consent collection for this funnel
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Require Consent Step Toggle */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="require-consent" className="text-slate-100 font-medium text-sm">
                Require Consent Step
              </Label>
              <p className="text-xs text-slate-500">
                Funnel must include an explicit consent step before lead submission
              </p>
            </div>
            <Switch
              id="require-consent"
              checked={compliance.requireConsentStep || false}
              onCheckedChange={(checked) => handleChange('requireConsentStep', checked)}
              data-testid="toggle-require-consent"
            />
          </div>
          
          {/* MA-ADMIN-013: Consent Version Catalog (replaces old static display) */}
          <div className="pt-4 border-t border-white/5">
            <ConsentVersionCatalog funnelId={funnelId} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// =============================================================================
// MA-COMPLIANCE-008: Simplified TrustedForm Setup Section
// =============================================================================
function TrustedFormSetupSection() {
  return (
    <div className="pt-3 border-t border-white/5">
      <p className="text-xs text-slate-500 uppercase tracking-wider mb-3">Setup Requirements</p>
      
      <ol className="text-sm text-slate-400 space-y-1.5 list-decimal list-inside mb-4">
        <li>Create a TrustedForm account</li>
        <li>Verify your domain ownership</li>
        <li>Enable Auto-Retain for your domain</li>
      </ol>
      
      <p className="text-xs text-amber-400/80 mb-4">
        MissionAssist does NOT retain certificates on your behalf.
      </p>
      
      <div className="flex flex-wrap gap-2">
        <a
          href="https://activeprospect.com/products/trustedform/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-slate-700/50 text-slate-300 hover:bg-slate-700 transition-colors"
          data-testid="link-create-account"
        >
          <ExternalLink className="h-3 w-3" />
          Create TrustedForm Account
        </a>
        <a
          href="https://support.activeprospect.com/hc/en-us/articles/44098193614996-Confirm-Domain-Ownership"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 transition-colors"
          data-testid="link-enable-autoretain"
        >
          <ExternalLink className="h-3 w-3" />
          Enable Auto-Retain Guide
        </a>
      </div>
    </div>
  );
}

// =============================================================================
// Runtime Section (Scaffolded)
// =============================================================================
function RuntimeSection({ settings, onSettingsChange }) {
  return (
    <div className="space-y-6" data-testid="runtime-section">
      <div>
        <div className="flex items-center gap-2">
          <Settings2 className="h-5 w-5 text-indigo-400" />
          <h2 className="text-xl font-bold text-slate-100">Runtime</h2>
        </div>
        <p className="text-slate-200/60 text-sm mt-1">
          Configure funnel runtime behavior and performance settings
        </p>
      </div>

      <Card className="glass border-white/10">
        <CardContent className="p-8">
          <div className="text-center">
            <Settings2 className="h-12 w-12 text-slate-600 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-slate-300 mb-2">Coming Soon</h3>
            <p className="text-slate-400 text-sm max-w-md mx-auto">
              Runtime settings including session timeout, retry behavior, and performance 
              optimizations will be available in a future update.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// =============================================================================
// Booking Section - MA-BOOK-002: Booking Health Panel
// =============================================================================
function BookingSection({ settings, onSettingsChange, funnelId }) {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Fetch booking health data
  const fetchBookingHealth = useCallback(async () => {
    if (!funnelId) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get(`/funnels/${funnelId}/booking-health`);
      if (response.data.success) {
        setHealthData(response.data);
      } else {
        setError('Failed to load booking health data');
      }
    } catch (err) {
      console.error('[BookingSection] Error fetching health:', err);
      setError(err.response?.data?.detail || 'Failed to connect to server');
    } finally {
      setLoading(false);
    }
  }, [funnelId]);
  
  useEffect(() => {
    fetchBookingHealth();
  }, [fetchBookingHealth]);
  
  // Determine global status
  const getGlobalStatus = () => {
    if (!healthData) return { status: 'unknown', label: 'Unknown', color: 'slate' };
    if (healthData.total === 0) return { status: 'none', label: 'No Booking Steps', color: 'slate' };
    if (healthData.is_ready) return { status: 'ready', label: 'Booking Ready', color: 'emerald' };
    return { status: 'action_required', label: 'Action Required', color: 'amber' };
  };
  
  const globalStatus = getGlobalStatus();
  
  return (
    <div className="space-y-6" data-testid="booking-section">
      <div>
        <div className="flex items-center gap-2">
          <Calendar className="h-5 w-5 text-indigo-400" />
          <h2 className="text-xl font-bold text-slate-100">Booking Health</h2>
        </div>
        <p className="text-slate-200/60 text-sm mt-1">
          Read-only overview of booking step configuration status
        </p>
      </div>

      {/* Loading State */}
      {loading && (
        <Card className="glass border-white/10">
          <CardContent className="p-8 flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-indigo-400" />
            <span className="ml-2 text-slate-400">Loading booking health...</span>
          </CardContent>
        </Card>
      )}
      
      {/* Error State */}
      {!loading && error && (
        <Card className="glass border-red-500/30 bg-red-500/5">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <XCircle className="h-5 w-5 text-red-400" />
              <div>
                <p className="text-red-200 font-medium">Failed to Load</p>
                <p className="text-red-300/70 text-sm">{error}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchBookingHealth}
                className="ml-auto text-red-300 hover:text-red-200"
              >
                <RefreshCw className="h-4 w-4 mr-1" />
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Health Data Display */}
      {!loading && !error && healthData && (
        <>
          {/* Global Status Card */}
          <Card className={cn(
            "border",
            globalStatus.status === 'ready' && "border-emerald-500/30 bg-emerald-500/5",
            globalStatus.status === 'action_required' && "border-amber-500/30 bg-amber-500/5",
            globalStatus.status === 'none' && "border-white/10 bg-white/[0.02]",
            globalStatus.status === 'unknown' && "border-white/10 bg-white/[0.02]"
          )} data-testid="booking-global-status">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={cn(
                    "w-12 h-12 rounded-full flex items-center justify-center",
                    globalStatus.status === 'ready' && "bg-emerald-500/20",
                    globalStatus.status === 'action_required' && "bg-amber-500/20",
                    globalStatus.status === 'none' && "bg-slate-500/20"
                  )}>
                    {globalStatus.status === 'ready' && (
                      <CheckCircle className="h-6 w-6 text-emerald-400" />
                    )}
                    {globalStatus.status === 'action_required' && (
                      <AlertTriangle className="h-6 w-6 text-amber-400" />
                    )}
                    {globalStatus.status === 'none' && (
                      <Calendar className="h-6 w-6 text-slate-400" />
                    )}
                  </div>
                  <div>
                    <h3 className={cn(
                      "text-lg font-semibold",
                      globalStatus.status === 'ready' && "text-emerald-200",
                      globalStatus.status === 'action_required' && "text-amber-200",
                      globalStatus.status === 'none' && "text-slate-300"
                    )}>
                      {globalStatus.label}
                    </h3>
                    <p className="text-slate-400 text-sm">
                      {healthData.total === 0 
                        ? "This funnel has no booking steps"
                        : globalStatus.status === 'ready'
                          ? "All booking steps are configured and ready"
                          : `${healthData.missing} booking step${healthData.missing !== 1 ? 's' : ''} need${healthData.missing === 1 ? 's' : ''} configuration`
                      }
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchBookingHealth}
                  className="text-slate-400 hover:text-slate-200"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {/* Summary Stats */}
          {healthData.total > 0 && (
            <div className="grid grid-cols-3 gap-4">
              <Card className="border-white/10 bg-white/[0.02]">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-slate-100">{healthData.total}</p>
                  <p className="text-xs text-slate-400 mt-1">Total Steps</p>
                </CardContent>
              </Card>
              <Card className="border-emerald-500/20 bg-emerald-500/5">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-emerald-300">{healthData.configured}</p>
                  <p className="text-xs text-emerald-400/70 mt-1">Configured</p>
                </CardContent>
              </Card>
              <Card className={cn(
                "border",
                healthData.missing > 0 ? "border-amber-500/20 bg-amber-500/5" : "border-white/10 bg-white/[0.02]"
              )}>
                <CardContent className="p-4 text-center">
                  <p className={cn(
                    "text-2xl font-bold",
                    healthData.missing > 0 ? "text-amber-300" : "text-slate-300"
                  )}>{healthData.missing}</p>
                  <p className={cn(
                    "text-xs mt-1",
                    healthData.missing > 0 ? "text-amber-400/70" : "text-slate-400"
                  )}>Missing</p>
                </CardContent>
              </Card>
            </div>
          )}
          
          {/* Per-Step Breakdown */}
          {healthData.nodes && healthData.nodes.length > 0 && (
            <Card className="border-white/10 bg-white/[0.02]">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-slate-300">
                  Booking Steps
                </CardTitle>
                <CardDescription className="text-xs text-slate-500">
                  Status of each booking step in this funnel
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-2">
                  {healthData.nodes.map((node, idx) => (
                    <div 
                      key={node.node_id}
                      className={cn(
                        "flex items-center justify-between p-3 rounded-lg",
                        node.status === 'configured' ? "bg-emerald-500/5" : "bg-amber-500/5"
                      )}
                      data-testid={`booking-node-${node.node_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium",
                          node.status === 'configured' 
                            ? "bg-emerald-500/20 text-emerald-300" 
                            : "bg-amber-500/20 text-amber-300"
                        )}>
                          {idx + 1}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-200">
                            {node.node_label}
                          </p>
                          <p className="text-xs text-slate-500">
                            {node.status === 'configured' 
                              ? `Calendar: ${node.calendar_id?.substring(0, 12)}...`
                              : "No calendar assigned"
                            }
                          </p>
                        </div>
                      </div>
                      <Badge 
                        variant="outline" 
                        className={cn(
                          "text-xs",
                          node.status === 'configured' 
                            ? "border-emerald-500/30 text-emerald-300 bg-emerald-500/10"
                            : "border-amber-500/30 text-amber-300 bg-amber-500/10"
                        )}
                      >
                        {node.status === 'configured' ? '✓ Configured' : '⚠ Missing'}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Info Note */}
          <Card className="border-white/5 bg-white/[0.01]">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Info className="h-4 w-4 text-slate-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-slate-500">
                  Booking configuration is managed in the Builder. Select a booking step 
                  and use the right panel to assign a calendar. This panel is read-only.
                </p>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

// =============================================================================
// Integrations Section (MA-UI-108: Outbound Routing)
// =============================================================================
function IntegrationsSection({ settings, onSettingsChange, funnelId }) {
  // Dynamically import OutboundRoutingSection to avoid circular deps
  const OutboundRoutingSection = React.lazy(() => 
    import('./OutboundRoutingSection')
  );
  
  return (
    <div className="space-y-6" data-testid="integrations-section">
      <div>
        <div className="flex items-center gap-2">
          <Plug2 className="h-5 w-5 text-indigo-400" />
          <h2 className="text-xl font-bold text-slate-100">Integrations</h2>
        </div>
        <p className="text-slate-200/60 text-sm mt-1">
          Connect this funnel to external systems and CRMs
        </p>
      </div>

      {/* MA-UI-108: Outbound Routing */}
      <React.Suspense fallback={
        <Card className="border-white/10 bg-white/[0.02]">
          <CardContent className="p-5 flex items-center justify-center py-8">
            <Loader2 className="h-4 w-4 animate-spin text-indigo-400" />
            <span className="ml-2 text-sm text-slate-400">Loading...</span>
          </CardContent>
        </Card>
      }>
        <OutboundRoutingSection 
          settings={settings} 
          onSettingsChange={onSettingsChange}
          funnelId={funnelId}
        />
      </React.Suspense>
    </div>
  );
}

// =============================================================================
// Main Settings Tab Component
// =============================================================================
export default function FunnelSettingsTab({ 
  funnelId, 
  settings = {}, 
  onSettingsChange,
  hasUnsavedChanges,
  funnel,  // MA-IMPL-117: Full funnel object for attribution
  onFunnelUpdate,  // MA-IMPL-117: Callback to update funnel fields
}) {
  const [activeSection, setActiveSection] = useState('compliance');
  
  // Initialize settings with defaults if not present
  // MA-COMPLIANCE-003: Removed trustedFormAccountId and trustedFormFieldName
  // MA-COMP-001: Updated defaults - TrustedForm off, requireConsentStep true
  useEffect(() => {
    if (!settings.compliance) {
      onSettingsChange({
        ...settings,
        compliance: {
          // MA-COMP-001: trustedformMode defaults to 'off' for lowest-friction onboarding
          trustedformMode: 'off',
          // MA-COMP-001: requireConsentStep defaults to true for safer default
          requireConsentStep: true,
          // MA-COMP-001: consentLanguageVersion is system-managed (read-only)
          consentLanguageVersion: null,
        },
      });
    }
  }, []);
  
  const renderSection = () => {
    switch (activeSection) {
      case 'compliance':
        return <ComplianceSection settings={settings} onSettingsChange={onSettingsChange} funnelId={funnelId} />;
      case 'attribution':
        // MA-IMPL-117: Attribution section for campaign assignment
        return <FunnelAttributionSection funnel={funnel} onFunnelUpdate={onFunnelUpdate} />;
      case 'runtime':
        return <RuntimeSection settings={settings} onSettingsChange={onSettingsChange} />;
      case 'booking':
        return <BookingSection settings={settings} onSettingsChange={onSettingsChange} funnelId={funnelId} />;
      case 'integrations':
        return <IntegrationsSection settings={settings} onSettingsChange={onSettingsChange} funnelId={funnelId} />;
      case 'enrichment':
        // MA-UI-109: Data Enrichment settings section
        return <DataEnrichmentSection settings={settings} onSettingsChange={onSettingsChange} />;
      default:
        return <ComplianceSection settings={settings} onSettingsChange={onSettingsChange} funnelId={funnelId} />;
    }
  };
  
  return (
    <div className="flex h-full" data-testid="funnel-settings-tab">
      {/* Left Navigation */}
      <div className="p-6 border-r border-white/10">
        <SettingsNav 
          activeSection={activeSection} 
          onSectionChange={setActiveSection} 
        />
      </div>
      
      {/* Main Content Area - MA-UI-109: Centered layout with max-width */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-[960px] mx-auto w-full px-4 sm:px-6">
          {renderSection()}
        </div>
      </div>
    </div>
  );
}
